import {
  Button,
  Row,
  Col,
  Input,
  Form,
  message,
  Space,
  Typography,
  Collapse,
  TimePicker,
} from 'antd';
import { arrayMoveImmutable } from 'array-move';
import MainPage from '@/components/page-prompt';
import { useDebounceFn } from 'ahooks';
import _ from 'lodash';
import {
  createXiaohongshuAccount,
  formatXiaohongshuConfig,
  getXiaohongshuDetail,
  updateXiahongshuAccount,
  getWorkerVersionMap,
} from '@/services/xiaohongshu';
import moment from 'moment';

import React, { useEffect, useState, useMemo, useRef } from 'react';
import Card, { createId } from '@/pages/xiaohongshu/components/card';
import Situation from '@/pages/xiaohongshu/components/situation';
import { useParams, history, useModel } from 'umi';
import Loading from '@/components/loading';
import JSONDrawer from '@/components/JSONDrawer';
import ReplaceComponent from './components/replace-component';

import { Access, useAccess } from 'umi';

const { Title } = Typography;
const { Panel } = Collapse;

enum CheckErrorMessage {
  笔记标题 = '笔记标题',
  主动打招呼 = '主动打招呼',
  联系电话 = '联系电话',
  二维码 = '二维码',
  引导语1 = '引导语1',
  引导语2 = '引导语2',
  附带引导语 = '附带引导语',
}

// 小红书 图片不能发链接 只能本地选取
// 实现思路 增加 二维码管理模
// 将根据二维码链接将图片下载至本地
// 配置主动打招呼
// 配置引导语

const wordsDefault = [
  {
    id: 'default',
    source: 'default',
    greeting: '',
    phone: '',
    qrcode: '',
    followup: '',
  },
];

const approacherDefault = [
  {
    id: 'default',
    source: 'default',
    greeting: 'default',
    phone: '',
    qrcode: '',
    followup: '',
  },
];

const Index: React.FC = () => {
  const [isPrompt, setIsPrompt] = useState<boolean>(true);
  const [words, setWords] = useState<API.Word[]>([]);
  const [situations, setSituations] = useState<API.Situation[]>([]);
  const [approaches, setApproaches] = useState<API.Approach[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [workerVersions, setWorkerVersions] = useState<API.SelectOption[]>([]);

  const [showFormatJSON, setShowFormatJSON] = useState<boolean>(false);
  const [formatJSON, setFormatJSON] = useState<JSON>();

  const [baseForm] = Form.useForm();
  const [commonForm] = Form.useForm();

  const { initialState } = useModel('@@initialState');
  const currentUser = initialState?.currentUser;

  const access = useAccess();

  const replaceRef = useRef<any>(null);

  const params: { id?: string } = useParams();
  const paramsId = useMemo(() => {
    return params && params && params?.id && params.id !== ':id'
      ? params.id
      : '';
  }, [params]);

  const isEdit = useMemo(() => {
    return Boolean(paramsId);
  }, [paramsId]);

  useEffect(() => {
    getWorker();
    if (paramsId) {
      getDetail(paramsId);
    } else {
      setWords(wordsDefault);
      setApproaches(approacherDefault);
    }
  }, []);

  const getWorker = async () => {
    const { resp } = await getWorkerVersionMap();
    if (resp?.code !== 0) return;
    setWorkerVersions(resp.data);
  };

  const getDetail = async (id: string) => {
    setLoading(true);
    const { resp, error } = await getXiaohongshuDetail({ id });
    setLoading(false);
    if (resp?.code !== 0) return message.error(error?.message);
    let { name, describe, xiaohongshuConfig, sxtId, sxtToken } = resp.data;
    let {
      email,
      password,
      commonFollowup,
      words,
      situations,
      approaches,
      cronTime,
    } = xiaohongshuConfig.config;
    baseForm.setFieldsValue({
      name,
      describe,
      email,
      password,
      sxtId,
      sxtToken,
      cronTime: moment(cronTime, 'HH:mm:ss'),
    });
    commonForm.setFieldsValue({ commonFollowup });
    setWords(words);
    setSituations(situations);
    setApproaches(approaches);
  };

  const { run: handleWordsChange } = useDebounceFn(
    (value: API.Word) => {
      let index = words.findIndex((w) => w.id === value.id);
      setWords((pre) => {
        pre[index] = value;
        return pre.slice();
      });
    },
    { wait: 10 },
  );

  const { run: handleSituationsChange } = useDebounceFn(
    (value: API.Situation) => {
      let index = situations.findIndex((s) => s.id === value.id);
      setSituations((pre) => {
        pre[index] = value;
        return pre.slice();
      });
    },
    { wait: 10 },
  );

  const { run: handleApproachesChange } = useDebounceFn(
    (value: API.Approach) => {
      let index = approaches.findIndex((s) => s.id === value.id);
      setApproaches((pre) => {
        pre[index] = value;
        return pre.slice();
      });
    },
    { wait: 10 },
  );

  const addWord = (word?: API.Word) => {
    let wordId = createId();
    if (!word)
      word = {
        id: wordId,
        source: '来源' + wordId,
        greeting: '',
        phone: '',
        qrcode: '',
        followup: '',
      };
    const arr = [...words, word];
    let _words = arrayMoveImmutable(arr, arr.length - 1, 1);
    setWords(_words);
    message.success('操作成功！');
  };

  const deleteWord = (wordId: string) => {
    if (wordId === 'default') return message.warning('兜底配置不能被删除！');
    let index = words.findIndex((w) => w.id === wordId);
    setWords((pre) => {
      _.pullAt(pre, index);
      return pre.slice();
    });
    message.success('删除成功！');
  };

  const addSituation = (situation?: API.Situation) => {
    let sId = createId();
    if (!situation)
      situation = {
        id: sId,
        keywords: '',
        phone: '',
        qrcode: '',
        followup: '',
      };
    const arr = [...situations, situation];
    let _situations = arrayMoveImmutable(arr, arr.length - 1, 1);
    setSituations(_situations);
    message.success('操作成功！');
  };
  const deleteSituation = (situationId: string) => {
    let index = situations.findIndex((w) => w.id === situationId);
    setSituations((pre) => {
      _.pullAt(pre, index);
      return pre.slice();
    });
    message.success('删除成功！');
  };

  const addApproach = (approach?: API.Approach) => {
    let approachId = createId();
    if (!approach)
      approach = {
        id: approachId,
        source: '来源' + approachId,
        greeting: '',
        phone: '',
        qrcode: '',
        followup: '',
      };
    const arr = [...approaches, approach];
    let _approaches = arrayMoveImmutable(arr, arr.length - 1, 1);
    setApproaches(_approaches);
    message.success('操作成功！');
  };
  const deleteApproach = (approachId: string) => {
    if (approachId === 'default')
      return message.warning('兜底配置不能被删除！');
    let index = approaches.findIndex((w) => w.id === approachId);
    setApproaches((pre) => {
      _.pullAt(pre, index);
      return pre.slice();
    });
    message.success('删除成功！');
  };

  const handleFormatConfig = async () => {
    const { name, describe, email, password } = baseForm.getFieldsValue();
    const { commonFollowup } = commonForm.getFieldsValue();

    const { resp, error } = await formatXiaohongshuConfig({
      email,
      password,
      words,
      situations,
      commonFollowup,
      approaches,
    });
    if (resp?.code !== 0) return message.error(error?.message);
    setFormatJSON(resp.data);
    setShowFormatJSON(true);
  };

  const handleSave = async () => {
    const { name, describe, email, password, cronTime, sxtId, sxtToken } =
      baseForm.getFieldsValue();
    const { commonFollowup } = commonForm.getFieldsValue();
    if (!name) return message.warning('请填写官号!');
    if (!email || !password) return message.warning('请填写邮箱和密码!');
    if (!cronTime) return message.warning('请设置机器人每日重启时间!');

    if (!checkWords('来源配置')) return;
    if (!checkSituations()) return;
    if (!checkWords('主动触发配置')) return;
    if (!commonFollowup) return message.warning('请完善引导语2！');

    // return

    let options = {
      name,
      describe,
      sxtId,
      sxtToken,
      userName: currentUser?.name,
      config: {
        email,
        password,
        cronTime: moment(cronTime).format('HH:mm:ss'),
        commonFollowup,
        words,
        situations,
        approaches,
      },
    };

    console.log(`isEdit======> ${isEdit}`);
    const { resp, error } = isEdit
      ? await updateXiahongshuAccount({ id: paramsId, data: options })
      : await createXiaohongshuAccount(options);
    if (resp?.code !== 0) return message.error(error?.message);
    message.success(resp?.message);
    handleBack();
  };
  const handleBack = () => {
    setIsPrompt(false);
    history.goBack();
  };

  const checkSituations = () => {
    let isSituationCheck = [true, ''];
    const situationsCheck: {
      prop: 'phone' | 'qrcode' | 'followup';
      label: string;
    }[] = [
      { prop: 'phone', label: CheckErrorMessage.联系电话 },
      { prop: 'qrcode', label: CheckErrorMessage.二维码 },
      { prop: 'followup', label: CheckErrorMessage.附带引导语 },
    ];

    for (let i = 0; i < situations.length; i++) {
      let vo = situations[i];
      for (let j = 0; j < situationsCheck.length; j++) {
        if (!isExist(vo[situationsCheck[j].prop])) {
          isSituationCheck = [false, `${situationsCheck[j].label}-关键字配置`];
          break;
        }
      }
    }
    if (!isSituationCheck[0]) message.warning(`请完善${isSituationCheck[1]}`);
    return isSituationCheck[0];
  };
  const isExist = (value: string) => {
    return Boolean(value);
  };
  const checkWords = (type: string = '来源配置') => {
    let isWordsCheck = [true, ''];
    let _words = approaches;
    const wordsCheck: { prop: 'source' | 'greeting'; label: string }[] = [
      { prop: 'greeting', label: CheckErrorMessage.主动打招呼 },
    ];

    if (type === '来源配置') {
      wordsCheck.push({ prop: 'source', label: CheckErrorMessage.笔记标题 });
      _words = words;
    }

    for (let i = 0; i < _words.length; i++) {
      let vo = _words[i];
      for (let j = 0; j < wordsCheck.length; j++) {
        if (!isExist(vo[wordsCheck[j].prop])) {
          isWordsCheck = [false, `${wordsCheck[j].label}-${vo.source}`];
          break;
        }
      }
      if (vo.source === 'default') {
        // if (!vo.phone) {
        //   isWordsCheck = [false, `${CheckErrorMessage.联系电话}-${vo.source}`]
        //   break
        // }
        if (!vo.qrcode) {
          isWordsCheck = [false, `${CheckErrorMessage.二维码}-${vo.source}`];
          break;
        }
        if (!vo.followup) {
          isWordsCheck = [false, `${CheckErrorMessage.引导语1}-${vo.source}`];
          break;
        }
      }
    }
    if (!isWordsCheck[0]) message.warning(`请完善${isWordsCheck[1]}-${type}`);
    return isWordsCheck[0];
  };

  const handleReplace = () => {
    if (replaceRef.current) replaceRef.current?.setVisible(true);
  };

  return (
    <MainPage isPrompt={isPrompt}>
      <div
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: 16,
        }}
      >
        <Space>
          <Button onClick={handleBack}>返回</Button>
          <Access accessible={access.xiaohongshuSave}>
            <Button type="primary" onClick={handleSave}>
              保存{isEdit && '编辑'}
            </Button>
          </Access>
        </Space>
        <Space>
          <Button onClick={handleFormatConfig}>查看格式化JSON</Button>
          <Button type="primary" onClick={handleReplace}>
            文案/二维码批量替换
          </Button>
        </Space>
      </div>
      <Collapse
        defaultActiveKey={['base', 'words', 'situations', 'approaches']}
      >
        <Panel header={<Title level={4}>基本信息</Title>} key="base">
          <Row>
            <Form form={baseForm} layout="inline">
              <Form.Item
                label="官号"
                name="name"
                rules={[{ required: true, message: '请输入官号!' }]}
              >
                <Input placeholder="请输入官号" />
              </Form.Item>
              <Form.Item
                label="sxtID"
                name="sxtId"
                rules={[{ required: false, message: 'cUserId(cookie)' }]}
              >
                <Input placeholder="cUserId(cookie)" />
              </Form.Item>
              <Form.Item
                label="sxtTOKEN"
                name="sxtToken"
                rules={[{ required: false, message: 'accessToken(cookie)' }]}
              >
                <Input placeholder="accessToken(cookie)" />
              </Form.Item>
              <Form.Item label="描述" name="describe">
                <Input placeholder="请输入描述" />
              </Form.Item>
              <Form.Item
                label="邮箱"
                name="email"
                rules={[{ required: true, message: '请输入邮箱!' }]}
              >
                <Input placeholder="请输入邮箱" />
              </Form.Item>
              <Form.Item
                label="密码"
                name="password"
                rules={[{ required: true, message: '请输入密码!' }]}
              >
                <Input placeholder="请输入密码" />
              </Form.Item>
              <Form.Item
                label="重启时间"
                name="cronTime"
                rules={[
                  { required: true, message: '请输入选择机器人重启时间!' },
                ]}
              >
                <TimePicker format="HH:mm:ss" />
              </Form.Item>
            </Form>
          </Row>
        </Panel>
        <Panel header={<Title level={4}>来源配置</Title>} key="words">
          <Space direction="vertical" style={{ width: '100%' }}>
            <Row>
              <Button type="primary" onClick={() => addWord()}>
                新增来源
              </Button>
            </Row>
            <Row>
              {words.map((vo, index) => (
                <Col span={6} key={vo.id} style={{ padding: 8 }}>
                  <Card
                    word={vo}
                    workerVersions={workerVersions}
                    handleWordsChange={handleWordsChange}
                    deleteWord={deleteWord}
                    copyWord={addWord}
                  />
                </Col>
              ))}
            </Row>
          </Space>
        </Panel>
        <Panel header={<Title level={4}>关键字配置</Title>} key="situations">
          <Space direction="vertical" style={{ width: '100%' }}>
            <Button type="primary" onClick={() => addSituation()}>
              新增关键字集合
            </Button>
            <Row>
              {situations.map((vo, index) => (
                <Col span={6} key={vo.id} style={{ padding: 8 }}>
                  <Situation
                    situation={vo}
                    workerVersions={workerVersions}
                    handleSituationsChange={handleSituationsChange}
                    deleteSituation={deleteSituation}
                    copySituation={addSituation}
                  />
                </Col>
              ))}
            </Row>
          </Space>
        </Panel>
        <Panel header={<Title level={4}>主动触发配置</Title>} key="approaches">
          <Space direction="vertical" style={{ width: '100%' }}>
            <Button type="primary" onClick={() => addApproach()}>
              新增主动触发
            </Button>
            <Row>
              <Form form={commonForm}>
                <Form.Item label="引导语2" name="commonFollowup">
                  <Input placeholder="请输入引导语2" style={{ width: 300 }} />
                </Form.Item>
              </Form>
            </Row>
            <Row>
              {approaches.map((vo, index) => (
                <Col span={6} key={vo.id} style={{ padding: 8 }}>
                  <Card
                    noSource={true}
                    word={vo}
                    workerVersions={workerVersions}
                    handleWordsChange={handleApproachesChange}
                    deleteWord={deleteApproach}
                    copyWord={addApproach}
                  />
                </Col>
              ))}
            </Row>
          </Space>
        </Panel>
      </Collapse>
      <Loading loading={loading} />
      <JSONDrawer
        title="格式化数据"
        json={formatJSON}
        visible={showFormatJSON}
        setIsShowJSON={() => setShowFormatJSON(false)}
      />
      <ReplaceComponent
        ref={replaceRef}
        followups={words}
        approaches={approaches}
        setFollowups={setWords}
        setApproaches={setApproaches}
        workerVersions={workerVersions}
      />
    </MainPage>
  );
};

export default Index;
